import React, { useState, useEffect } from "react";
import serviceApi from "../../../services/serviceApi";
import environment from "../../../environments/environment";

interface Notification {
  type: string;
  dateTime: string;
  message: string;
  attachmentUrl: string;
}

interface NotificationContentProps {
  student: { name: string; clss: string };
  school: { name: string; logo: string };
}

const NotificationContent: React.FC<NotificationContentProps> = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAttachmentPopup, setShowAttachmentPopup] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<{ title: string, attachmentUrl: string } | null>(null);

  useEffect(() => {
    const fetchNotifications = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await serviceApi.get(environment.notifications);
        if (response.status === "SUCCESS" && response.dataList) {
          setNotifications(response.dataList);
        } else {
          setError(response.message || "Failed to fetch notifications");
        }
      } catch (err) {
        setError('An error occurred while fetching notifications');
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotifications();
  }, []);

  const isPdfFile = (url: string) => url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('pdf');
  const isImageFile = (url: string) => url.toLowerCase().startsWith('data:image/') || /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/.test(url.toLowerCase());

  const openAttachment = (att: { title: string, attachmentUrl: string }) => {
    if (isPdfFile(att.attachmentUrl)) {
      window.open(att.attachmentUrl, '_blank');
      return;
    }
    setSelectedAttachment(att);
    setShowAttachmentPopup(true);
  };

  const renderAttachmentPreview = (attachment: { title: string, attachmentUrl: string }) => {
    if (isPdfFile(attachment.attachmentUrl)) {
      return (
        <div className="attachment-preview pdf-preview" onClick={() => openAttachment(attachment)}>
          <i className="fa-solid fa-file-pdf pdf-icon"></i>
        </div>
      );
    } else if (isImageFile(attachment.attachmentUrl)) {
      return (
        <div className="attachment-preview image-preview" onClick={() => openAttachment(attachment)}>
          <img src={attachment.attachmentUrl} alt={attachment.title} className="attachment-thumbnail" />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="notification-page">
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-overlay__content">
            <div className="spinner spinner--lg"></div>
            <span className="loading-text">Loading notifications...</span>
          </div>
        </div>
      )}

      {/* Title using Part 2 Style */}
      <h2 className="title-text notification-heading">Notifications</h2>

      {error && <p className="error-text">{error}</p>}

      {!isLoading && !error && notifications.length > 0 && (
        notifications.map((note, index) => (
          <div key={index} className="event-container" style={{ marginBottom: 'var(--sz-16)' }}>

            {/* TOP ROW: Date Left, Type Right */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="event-date" style={{
                color: 'var(--colorAccentDark)', // This is the Red/Orange from your Part 1
                fontWeight: '700',
                fontStyle: 'italic',
                fontSize: 'var(--sz-14)'
              }}>
                {note.dateTime?.split(' ')[0]}
              </span>

              <span className="nav-item" style={{ fontSize: 'var(--sz-12)', fontWeight: 'bold' }}>
                [{note.type?.toUpperCase()}]
              </span>
            </div>

            {/* DIVIDER: Using Part 2 Divider */}
            <div className="horizontal-divider" style={{ margin: 'var(--sz-6) 0 var(--sz-10) 0' }}></div>

            {/* MESSAGE BODY */}
            <div className="event-item" style={{ borderBottom: 'none', padding: 0 }}>
              {note.message && (
                <p className="event-description" style={{ margin: 0, lineHeight: '1.5' }}>
                  {note.message}
                </p>
              )}

              {note.attachmentUrl && (
                <div className="attachment-section">
                  {renderAttachmentPreview({
                    title: `${note.type} - ${note.dateTime}`,
                    attachmentUrl: note.attachmentUrl
                  })}
                </div>
              )}
            </div>
          </div>
        ))
      )}

      {!isLoading && !error && notifications.length === 0 && (
        <p className="no-events">No notifications available</p>
      )}

      {/* ATTACHMENT POPUP using Part 3 Styles */}
      {showAttachmentPopup && selectedAttachment && (
        <div className="popup-overlay">
          <div className="popup-box attachment-popup">
            <div className="popup-header">
              <h3 className="popup-title">{selectedAttachment.title}</h3>
              <button className="close-popup-btn" onClick={() => setShowAttachmentPopup(false)}>
                &times;
              </button>
            </div>
            <div className="attachment-popup-content" style={{ textAlign: 'center' }}>
              <img src={selectedAttachment.attachmentUrl} alt="Preview" className="attachment-image" style={{ maxHeight: '60vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationContent;